import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Badge } from 'primereact/badge';
import BaseCard from '../../components/misc/BaseCard.jsx';
import BaseOverlay from '../../components/layout/BaseOverlay.jsx';
import BaseTreeSelect from '../../components/form/BaseTreeSelect.jsx';
import IconAdd from '../../components/images/IconAdd.jsx';
import IconClose from '../../components/images/IconClose.jsx';
import { addTreeSelection, findItem, removeAttribute } from '../../utils/helper.js';
import { useGetSkillsQuery } from '../../api/misc.js';

const NeedsAndSkillsDuplicates = ({ control, clearErrors }) => {
    const {
        fields: fieldsNeeds,
        append: appendNeed,
        remove: removeNeed
    } = useFieldArray({
        control,
        name: 'needs'
    });
    const {
        fields: fieldsSkills,
        append: appendSkill,
        remove: removeSkill
    } = useFieldArray({
        control,
        name: 'skills'
    });

    const treeElement = useCallback(
        (key, item) => ({
            key,
            label: item.name,
            data: item.id,
            ...(item.sub_skills && item.sub_skills.length > 0
                ? { children: item.sub_skills.map((sub, idx) => treeElement(`${key}-${idx}`, sub)) }
                : {})
        }),
        []
    );

    const { data: skillsData } = useGetSkillsQuery({});
    const skillsTree = useMemo(() => {
        let arr = [];
        if (skillsData?.response?.skills) {
            skillsData.response.skills.forEach((item, index) => {
                arr.push(treeElement(index, item));
            });
        }
        return arr;
    }, [skillsData, treeElement]);

    const [showOverlay, setShowOverlay] = useState(false);
    const [activeOverlayType, setActiveOverlayType] = useState('');
    const [selectedNeeds, setSelectedNeeds] = useState({});
    const [selectedSkills, setSelectedSkills] = useState({});

    const setAttribute = (treeArray, field, func, property) => {
        let arr = {};
        const processNode = (node, selectedIds) => {
            let key = findItem(node, node.data);
            if (key === undefined || key === null) return { checked: false, partialChecked: false };
            if (node.children && node.children.length > 0) {
                let childrenStates = node.children.map((child) => processNode(child, selectedIds));
                let allChildrenChecked = childrenStates.every((child) => child.checked);
                let someChildrenChecked = childrenStates.some((child) => child.checked || child.partialChecked);
                arr[key] = { checked: allChildrenChecked, partialChecked: someChildrenChecked && !allChildrenChecked };
                return arr[key];
            } else {
                let isSelected = field.some((item) => item[property] === node.data);
                arr[key] = { checked: isSelected, partialChecked: false };
                return arr[key];
            }
        };

        treeArray.forEach((node) =>
            processNode(
                node,
                field.map((item) => item[property])
            )
        );
        func(arr);
    };

    useEffect(() => {
        if (skillsTree.length > 0) {
            setAttribute(skillsTree, fieldsNeeds, setSelectedNeeds, 'need_id');
        }
    }, [skillsTree, fieldsNeeds]);

    useEffect(() => {
        if (skillsTree.length > 0) {
            setAttribute(skillsTree, fieldsSkills, setSelectedSkills, 'skill_id');
        }
    }, [skillsTree, fieldsSkills]);

    const openOverlay = (type) => {
        setActiveOverlayType(type);
        setShowOverlay(true);
    };

    const closeOverlay = () => {
        setShowOverlay(false);
        setActiveOverlayType('');
    };

    return (
        <>
            <BaseCard className='border-0 bg-theme1-tan p-0 pb-6'>
                <div className='px-8 flex items-center justify-between p-6'>
                    <div className='text-2xl font-bold text-theme1-title2'>Needs &amp; Skills</div>
                </div>

                <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                    <div className='max-w-3xl w-full mx-auto px-4'>
                        <Accordion className='custom-accordion' activeIndex={0}>
                            <AccordionTab
                                key='needs-tab'
                                className='custom-accordion-tab-no-padding'
                                header={
                                    <>
                                        <span className='text-lg font-bold text-theme1-primary'>Needs</span>
                                        <Badge value={fieldsNeeds?.length} className='ns-badge' />
                                    </>
                                }
                            >
                                <div className='flex items-center flex-wrap'>
                                    {fieldsNeeds.length > 0 &&
                                        fieldsNeeds.map((need, index) => (
                                            <span
                                                key={need.id}
                                                className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center w-fit'
                                            >
                                                <span>{need.name}</span>
                                                <span
                                                    onClick={() =>
                                                        removeAttribute(
                                                            skillsTree,
                                                            index,
                                                            need.need_id,
                                                            setSelectedNeeds,
                                                            'need_id',
                                                            fieldsNeeds,
                                                            removeNeed
                                                        )
                                                    }
                                                >
                                                    <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                                </span>
                                            </span>
                                        ))}
                                    <span
                                        onClick={() => openOverlay('needs')}
                                        className='mr-4 mb-4 px-4 py-2 border-2 border-dashed border-theme1-gold1 rounded-4xl text-theme1-gold1 font-bold flex items-center w-fit'
                                    >
                                        <span>Add Need</span>
                                        <span>
                                            <IconAdd className='w-6 h-6 fill-theme1-gold1' />
                                        </span>
                                    </span>
                                </div>
                            </AccordionTab>
                        </Accordion>
                    </div>
                </BaseCard>

                <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                    <div className='max-w-3xl w-full mx-auto px-4'>
                        <Accordion className='custom-accordion' multiple={false} activeIndex={0}>
                            <AccordionTab
                                key='capabilities-tab'
                                className='custom-accordion-tab-no-padding'
                                header={
                                    <>
                                        <span className='text-lg font-bold text-theme1-primary'>Skills</span>
                                        <Badge value={fieldsSkills?.length} className='ns-badge' />
                                    </>
                                }
                            >
                                <div className='flex items-center flex-wrap'>
                                    {fieldsSkills.length > 0 &&
                                        fieldsSkills.map((skill, index) => (
                                            <span
                                                key={skill.id}
                                                className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center w-fit'
                                            >
                                                <span>{skill.name}</span>
                                                <span
                                                    onClick={() =>
                                                        removeAttribute(
                                                            skillsTree,
                                                            index,
                                                            skill.skill_id,
                                                            setSelectedSkills,
                                                            'skill_id',
                                                            fieldsSkills,
                                                            removeSkill
                                                        )
                                                    }
                                                >
                                                    <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                                </span>
                                            </span>
                                        ))}
                                    <span
                                        onClick={() => openOverlay('skills')}
                                        className='mr-4 mb-4 px-4 py-2 border-2 border-dashed border-theme1-gold1 rounded-4xl text-theme1-gold1 font-bold flex items-center w-fit'
                                    >
                                        <span>Add Skill</span>
                                        <span>
                                            <IconAdd className='w-6 h-6 fill-theme1-gold1' />
                                        </span>
                                    </span>
                                </div>
                            </AccordionTab>
                        </Accordion>
                    </div>
                </BaseCard>
            </BaseCard>

            <BaseOverlay show={showOverlay} className='overflow-auto h-[100dvh] rounded-none'>
                <div className='min-h-96 max-w-3xl w-full mx-auto px-4'>
                    <div className='flex justify-end py-4'>
                        <div className='text-theme1-primary font-bold' onClick={closeOverlay}>
                            Close
                        </div>
                    </div>
                    <div className='text-center text-theme1-title2 font-bold text-[22px] mb-3'>
                        {activeOverlayType === 'needs' ? 'Needs' : 'Skills'}
                    </div>
                    {activeOverlayType === 'needs' && (
                        <div>
                            <div className='mb-4'>
                                <div className='card flex justify-content-center'>
                                    <BaseTreeSelect
                                        name='needs'
                                        label='Select Needs'
                                        inputClassName='md:w-20rem w-full'
                                        value={selectedNeeds}
                                        setValue={setSelectedNeeds}
                                        options={skillsTree}
                                        selectionMode='checkbox'
                                        display='chip'
                                        placeholder='Choose needs'
                                        showClear={false}
                                        clearErrors={clearErrors}
                                        filter={true}
                                        filterMode='strict'
                                        filterDelay={10}
                                        onSelect={(value) =>
                                            addTreeSelection(
                                                skillsTree,
                                                'needs',
                                                value,
                                                'need_id',
                                                appendNeed,
                                                removeNeed,
                                                fieldsNeeds,
                                                setSelectedNeeds
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            <div className='flex items-center flex-wrap overflow-y-scroll'>
                                {fieldsNeeds.length > 0 &&
                                    fieldsNeeds.map((need, index) => (
                                        <span
                                            key={need.id}
                                            className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                        >
                                            <span>{need.name}</span>
                                            <span
                                                onClick={() =>
                                                    removeAttribute(
                                                        skillsTree,
                                                        index,
                                                        need.need_id,
                                                        setSelectedNeeds,
                                                        'need_id',
                                                        fieldsNeeds,
                                                        removeNeed
                                                    )
                                                }
                                            >
                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                            </span>
                                        </span>
                                    ))}
                            </div>
                        </div>
                    )}
                    {activeOverlayType === 'skills' && (
                        <div>
                            <div className='mb-4'>
                                <div className='card flex justify-content-center'>
                                    <BaseTreeSelect
                                        name='skills'
                                        label='Select Skills'
                                        inputClassName='md:w-20rem w-full'
                                        value={selectedSkills}
                                        setValue={setSelectedSkills}
                                        options={skillsTree}
                                        selectionMode='checkbox'
                                        display='chip'
                                        placeholder='Choose skills'
                                        showClear={false}
                                        clearErrors={clearErrors}
                                        filter={true}
                                        filterMode='strict'
                                        filterDelay={10}
                                        onSelect={(value) =>
                                            addTreeSelection(
                                                skillsTree,
                                                'skills',
                                                value,
                                                'skill_id',
                                                appendSkill,
                                                removeSkill,
                                                fieldsSkills,
                                                setSelectedSkills
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            <div className='flex items-center flex-wrap overflow-y-scroll'>
                                {fieldsSkills.length > 0 &&
                                    fieldsSkills.map((skill, index) => (
                                        <span
                                            key={skill.id}
                                            className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                        >
                                            <span>{skill.name}</span>
                                            <span
                                                onClick={() =>
                                                    removeAttribute(
                                                        skillsTree,
                                                        index,
                                                        skill.skill_id,
                                                        setSelectedSkills,
                                                        'skill_id',
                                                        fieldsSkills,
                                                        removeSkill
                                                    )
                                                }
                                            >
                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                            </span>
                                        </span>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            </BaseOverlay>
        </>
    );
};

export default NeedsAndSkillsDuplicates;
